// @ts-strict-ignore
import { AppStoreBuild, IntegrationType, StabilityInfo, StabilityIssue } from '../../types';
import * as React from 'react';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import List from 'antd/lib/list';
import StabilityMetrics from './StabilityMetrics';
import { antGrey, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import StabilityIssues from '../stability/StabilityIssues';
import Space from 'antd/lib/space';
import {
    getStabilityInfos,
    nonFatalStabilityIssuesSupportedForIntegration,
    stabilityIssuesSupportedForIntegration,
} from '../../helpers';
import AppStoreBuildComponent from '../appStoreBuilds/AppStoreBuildComponent';
import StabilityIssueDrawer from '../stability/StabilityIssueDrawer';
import { selectVCSIntegration, selectStabilityIntegrations } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import Alert from 'antd/lib/alert';

interface StabilityMetricsProps {
    appId: string;
    minWidth?: number | string;
    maxWidth?: number;
    titleStyle?: React.CSSProperties;
    backgroundColor: string;
    isRunwayAutoPaused?: boolean;
    builds: AppStoreBuild[];
    shouldHideIntegrationTitle?: boolean;
}

const StabilityMetricsContainer: React.FC<StabilityMetricsProps> = (props: StabilityMetricsProps) => {
    const hasBuilds = props.builds.length > 0;
    // Header shows icons for every stab-mon integration present across the displayed builds.
    const primaryBuildStabilityInfos = getStabilityInfos(props.builds[0]);
    // Deduplicate integration types across all stability infos on the primary build for the title icons.
    const headerIntegrationTypes = Array.from(new Set(primaryBuildStabilityInfos.map((info) => info.integrationId)));

    const stabilityIntegrationsAreInError = useSelector((state: RootState) => {
        const stabIntegrations = selectStabilityIntegrations(state, props.appId);
        return stabIntegrations.some((integration) => !!integration.integrationError);
    });

    const baseTitleStyle: React.CSSProperties = { ...titleStyle, color: antGrey.grey7, fontSize: 12 };

    const count = props.builds.length;

    // Environment is currently shown only when all infos on the primary build share the same environment name.
    const primaryEnvironments = Array.from(
        new Set(primaryBuildStabilityInfos.map((info) => info.environmentName).filter((env) => !!env)),
    );
    const environment = hasBuilds && primaryEnvironments.length === 1 ? primaryEnvironments[0] : null;
    const stabTitleStyle = props.titleStyle ?? baseTitleStyle;

    const shouldShowIntegrationTitle = !props.shouldHideIntegrationTitle;
    const primaryBuildLinks = count === 1 ? primaryBuildStabilityInfos.filter((info) => !!info.url) : [];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 20 }}>
            {stabilityIntegrationsAreInError && (
                <Alert
                    type="warning"
                    message={`There's an error with your stability monitoring integration. Runway may not reflect the most up to date data.`}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                {shouldShowIntegrationTitle && (
                    <>
                        {headerIntegrationTypes.length > 0 && (
                            <Space size={4}>
                                {headerIntegrationTypes.map((type) => {
                                    const [icon, alt] = iconAndAltForIntegrationType(type);
                                    if (!icon) return null;
                                    return <img key={type} src={icon} alt={alt} width={30} height={30} />;
                                })}
                            </Space>
                        )}
                        <Text style={stabTitleStyle}>
                            Stability
                            {environment && (
                                <Text
                                    style={{
                                        ...stabTitleStyle,
                                        color: antGrey.grey6,
                                        textTransform: 'capitalize',
                                        fontWeight: 300,
                                    }}
                                >
                                    {' '}
                                    - {environment}
                                </Text>
                            )}
                        </Text>
                    </>
                )}
                {primaryBuildLinks.length > 0 && (
                    <Space size={8} style={{ marginLeft: 'auto' }} wrap>
                        {primaryBuildLinks.map((info) => (
                            <ViewInStabilityPlatformButton
                                key={`${info.integrationId}-${info.buildIdentifier ?? ''}`}
                                stabilityInfo={info}
                            />
                        ))}
                    </Space>
                )}
            </div>
            <StabilityMetricsContainerBase {...props} />
        </div>
    );
};

export const StabilityMetricsContainerBase: React.FC<StabilityMetricsProps> = (props: StabilityMetricsProps) => {
    const [drawerState, setDrawerState] = React.useState<{
        issue: StabilityIssue;
        integrationId: IntegrationType;
    } | null>(null);
    const showBuildInfo = props.builds.length > 1;

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const count = props.builds.length;

    return (
        <>
            <List
                split
                style={{ flexGrow: 1, minWidth: 0 }}
                itemLayout="vertical"
                dataSource={props.builds}
                renderItem={(build, index) => {
                    const stabilityInfos = getStabilityInfos(build);
                    const isFirst = index === 0;
                    const isLast = index === count - 1;

                    return (
                        <List.Item key={index} style={{ paddingTop: isFirst ? 0 : 16, paddingBottom: isLast ? 0 : 16 }}>
                            <Space direction="vertical" style={{ alignItems: 'stretch', width: '100%' }} size={16}>
                                {showBuildInfo && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div>{<AppStoreBuildComponent {...build} />}</div>
                                        {stabilityInfos.some((info) => !!info.url) && (
                                            <Space size={8} wrap>
                                                {stabilityInfos
                                                    .filter((info) => !!info.url)
                                                    .map((info) => (
                                                        <ViewInStabilityPlatformButton
                                                            key={`${info.integrationId}-${info.buildIdentifier ?? ''}`}
                                                            stabilityInfo={info}
                                                        />
                                                    ))}
                                            </Space>
                                        )}
                                    </div>
                                )}
                                {stabilityInfos.map((stabilityInfo) => {
                                    const [icon, alt] = iconAndAltForIntegrationType(stabilityInfo.integrationId);
                                    return (
                                        <Space
                                            key={`${stabilityInfo.integrationId}-${stabilityInfo.buildIdentifier ?? ''}`}
                                            direction="vertical"
                                            style={{ alignItems: 'stretch', width: '100%' }}
                                            size={12}
                                        >
                                            {stabilityInfos.length > 1 && (
                                                <Space size={6} align="center">
                                                    {icon && <img src={icon} alt={alt} width={18} height={18} />}
                                                    <Text style={{ color: antGrey.grey8, fontWeight: 500 }}>
                                                        {displayNameForIntegrationType(stabilityInfo.integrationId)}
                                                    </Text>
                                                </Space>
                                            )}
                                            <StabilityMetrics
                                                {...stabilityInfo}
                                                isRunwayAutoPaused={props.isRunwayAutoPaused}
                                                backgroundColor={props.backgroundColor}
                                            />
                                            {stabilityIssuesSupportedForIntegration(stabilityInfo.integrationId) && (
                                                <StabilityIssues
                                                    topNewStabilityIssues={stabilityInfo.topNewStabilityIssues}
                                                    topStabilityIssues={stabilityInfo.topStabilityIssues}
                                                    topNewStabilityIssuesNonFatal={
                                                        stabilityInfo.topNewStabilityIssuesNonFatal
                                                    }
                                                    topStabilityIssuesNonFatal={
                                                        stabilityInfo.topStabilityIssuesNonFatal
                                                    }
                                                    backgroundColor={props.backgroundColor}
                                                    shouldShowNonFatals={nonFatalStabilityIssuesSupportedForIntegration(
                                                        stabilityInfo.integrationId,
                                                    )}
                                                    onShowDrawerForIssue={(issue) =>
                                                        setDrawerState({
                                                            issue,
                                                            integrationId: stabilityInfo.integrationId,
                                                        })
                                                    }
                                                />
                                            )}
                                        </Space>
                                    );
                                })}
                            </Space>
                        </List.Item>
                    );
                }}
                locale={{ emptyText: 'No stability data found' }}
            ></List>
            <StabilityIssueDrawer
                open={!!drawerState}
                integrationId={drawerState?.integrationId}
                issue={drawerState?.issue}
                onClose={() => setDrawerState(null)}
                vcsId={vcsIntegration?.id}
                appId={props.appId}
            />
        </>
    );
};

interface ViewInStabilityPlatformButtonProps {
    stabilityInfo: StabilityInfo;
}

export const ViewInStabilityPlatformButton: React.FC<ViewInStabilityPlatformButtonProps> = ({ stabilityInfo }) => {
    if (!stabilityInfo?.url) {
        return null;
    }
    return (
        <Button type="link" href={stabilityInfo.url} target="_blank" style={{ padding: 0 }}>
            View in {displayNameForIntegrationType(stabilityInfo.integrationId)}
        </Button>
    );
};

export default StabilityMetricsContainer;
